import { logError } from "../../utils/error";
import { getTimeRangeFilter, TimeRange } from "../../utils/timerange";
import { supabase } from "../supabase/client";

export interface ScorecardAnalysisData {
  id: string;
  scorecard_id: string;
  interaction_id: string;
  attribute: string;
  summary: string;
  score: string;
  score_value: number;
  score_justification: string;
  next_time: string;
  training_suggestions: string;
  details: any;
  created: string;
  user_email: string;
  lens_name: string;
}

export interface CoachingDataParams {
  timeRange: TimeRange;
  timezone: string;
  lensFilter: string;
  userFilter?: string;
  permittedEmails?: string[] | null;
  attributeFilter?: string;
  minScore?: number;
  maxScore?: number;
  limit?: number;
  tenantId: string | null;
}

export const getScorecardAnalysisData = async ({
  timeRange,
  timezone,
  lensFilter,
  userFilter = "all",
  permittedEmails = null,
  attributeFilter,
  minScore,
  maxScore,
  limit,
  tenantId
}: CoachingDataParams): Promise<ScorecardAnalysisData[]> => {
  try {
    const timeRangeParams = getTimeRangeFilter(timeRange, timezone);

    const { data, error } = await supabase.rpc("get_scorecard_analysis_data", {
      p_tenant_id: tenantId as string, // TODO(types): tenantId can be null; RPC signature requires string — runtime behavior unchanged
      p_start_date: timeRangeParams.startDate,
      p_end_date: timeRangeParams.endDate,
      p_lens_filter: lensFilter === "all" ? undefined : lensFilter,
      p_user_filter: userFilter === "all" ? undefined : userFilter,
      p_permitted_emails: permittedEmails ?? undefined,
      p_attribute_filter: attributeFilter ?? undefined,
      p_min_score: minScore ?? undefined,
      p_max_score: maxScore ?? undefined,
      p_limit: limit ?? undefined
    });

    if (error) {
      throw new Error(`Failed to fetch scorecard analysis data: ${error.message}`);
    }

    type ScorecardAnalysisDataRow = {
      id: string;
      scorecard_id: string;
      interaction_id: string | null;
      attribute: string;
      summary: string;
      score: string;
      score_value: number;
      score_justification: string;
      next_time: string;
      training_suggestions: string;
      details: unknown;
      created: string;
      user_email: string | null;
      lens_name: string | null;
    };

    return ((data ?? []) as unknown as ScorecardAnalysisDataRow[]).map((item) => ({
      id: item.id,
      scorecard_id: item.scorecard_id,
      interaction_id: item.interaction_id ?? "",
      attribute: item.attribute,
      summary: item.summary,
      score: item.score,
      score_value: item.score_value,
      score_justification: item.score_justification,
      next_time: item.next_time,
      training_suggestions: item.training_suggestions,
      details: item.details,
      created: item.created,
      user_email: item.user_email ?? "",
      lens_name: item.lens_name ?? ""
    }));
  } catch (error) {
    logError("Error fetching scorecard analysis data", error);
    throw error;
  }
};
